import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Table, Button, Tag, Select, Row, Col, Tooltip, Modal, Timeline, Spin, message } from 'antd';
import {
  HistoryOutlined,
  EyeOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  PlusCircleOutlined,
  EditOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  PushpinOutlined,
  MessageOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { getBookingsByType } from '../../../api/commonAPI';
import { 
  fetchAuditLogsByBookingId, 
  clearAuditLogs 
} from '../../../redux/auditLog/auditLogActions';
import '../../../assets/scss/components/AuditTracking/AuditTrackingPage.scss';

const { Option } = Select;

const AuditTrackingPage = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const { auditLogs, loading: loadingAudit } = useSelector(state => state.auditLog);
  
  // Local state
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBookingType, setSelectedBookingType] = useState('OFFLINE_BOOKING');
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, [selectedBookingType]);

  useEffect(() => {
    // Clear audit logs when component unmounts
    return () => {
      dispatch(clearAuditLogs());
    };
  }, [dispatch]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await getBookingsByType(selectedBookingType);
      setBookings(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      message.error('Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAuditLog = (booking) => {
    setSelectedBooking(booking);
    setAuditModalVisible(true);
    
    // Dispatch Redux action to fetch audit logs
    dispatch(fetchAuditLogsByBookingId(booking.id || booking._id));
  };

  const getBookingTypeTag = (type) => {
    const colors = {
      'OFFLINE_BOOKING': 'purple',
      'SYSTEM_BOOKING': 'blue',
      'MANUAL_BOOKING': 'orange',
      'GOOGLE_CALENDAR_EVENT': 'green'
    };
    return <Tag color={colors[type] || 'default'}>{type}</Tag>;
  };

  const getBookingStatusTag = (status) => {
    const statusColors = {
      'INITIATED': 'orange',
      'TENTATIVE': 'gold',
      'CONFIRMED': 'green',
      'COMPLETED': 'blue',
      'CANCELLED': 'red'
    };
    return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
  };

  const getAuditActionColor = (action) => {
    const colors = {
      'CREATED': 'green',
      'UPDATED': 'blue',
      'CANCELLED': 'red',
      'CONFIRMED': 'green',
      'PAYMENT_UPDATED': 'purple'
    };
    return colors[action] || 'gray';
  };

  const getAuditActionIcon = (action) => {
    const iconMap = {
      'CREATED': <PlusCircleOutlined />,
      'UPDATED': <EditOutlined />,
      'CANCELLED': <CloseCircleOutlined />,
      'CONFIRMED': <CheckCircleOutlined />,
      'PAYMENT_UPDATED': <DollarOutlined />
    };
    return iconMap[action] || <PushpinOutlined />;
  };

  const renderChangeDetails = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <span style={{ color: '#8c8c8c' }}>No detailed changes recorded</span>;
    }

    // Helper function to format date values
    const formatValue = (key, val) => {
      // Check if the field is a date field and value looks like an ISO date
      const dateFields = ['bookingStartDate', 'bookingEndDate', 'startDate', 'endDate', 'createdDate', 'modifiedDate', 'checkInDate', 'checkOutDate'];
      const isDateField = dateFields.some(field => key.toLowerCase().includes(field.toLowerCase()));
      
      if (isDateField && val && typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T/)) {
        return moment(val).format('DD MMM YYYY HH:mm');
      }
      
      return String(val);
    };

    return (
      <div style={{ marginTop: 8 }}>
        {Object.entries(changes).map(([key, value]) => {
          if (typeof value === 'object' && value.old !== undefined && value.new !== undefined) {
            return (
              <div key={key} style={{ marginBottom: 4 }}>
                <strong>{key}:</strong> 
                <span style={{ color: '#ff4d4f' }}> {formatValue(key, value.old)}</span> → 
                <span style={{ color: '#52c41a' }}> {formatValue(key, value.new)}</span>
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: 4 }}>
              <strong>{key}:</strong> {formatValue(key, value)}
            </div>
          );
        })}
      </div>
    );
  };

  const columns = [
    {
      title: 'Booking Ref',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text, record) => {
        const ref = record.bookingRef || text?.substring(0, 8) || record._id?.substring(0, 8);
        return <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{ref}</span>;
      }
    },
    {
      title: 'Booking Type',
      dataIndex: 'bookingType',
      key: 'bookingType',
      width: 150,
      render: (type) => getBookingTypeTag(type)
    },
    {
      title: 'Client',
      key: 'client',
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div><UserOutlined /> {record.user?.name || record.clientName || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {record.user?.email || record.clientEmail || ''}
          </div>
        </div>
      )
    },
    {
      title: 'Space',
      dataIndex: 'title',
      key: 'spaceName',
      ellipsis: true,
      render: (text, record) => text || record.spaceName || '-'
    },
    {
      title: 'Created By',
      key: 'createdBy',
      width: 180,
      render: (_, record) => {
        if (record.createdUser) {
          return (
            <div>
              <div style={{ fontWeight: 500 }}>{record.createdUser.name || 'N/A'}</div>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                {record.createdUser.email || ''}
              </div>
            </div>
          );
        }
        return <span style={{ color: '#8c8c8c' }}>-</span>;
      }
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 160,
      render: (date) => date ? moment(date).format('DD MMM YYYY HH:mm') : '-'
    },
    {
      title: 'Last Modified',
      key: 'lastModified',
      width: 180,
      render: (_, record) => {
        if (record.lastModifiedDate) {
          return (
            <div>
              <div><ClockCircleOutlined /> {moment(record.lastModifiedDate).format('DD MMM YYYY HH:mm')}</div>
              {record.lastUpdatedUser && (
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  By: {record.lastUpdatedUser.name || record.lastUpdatedUser.email}
                </div>
              )}
            </div>
          );
        }
        return <span style={{ color: '#8c8c8c' }}>-</span>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'bookingStatus',
      key: 'bookingStatus',
      width: 120,
      render: (status) => getBookingStatusTag(status)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="View Audit History">
          <Button
            type="primary"
            icon={<HistoryOutlined />}
            onClick={() => handleViewAuditLog(record)}
            size="small"
          >
            History
          </Button>
        </Tooltip>
      )
    }
  ];

  return (
    <div className="audit-tracking-page">
      <Card
        title={
          <span>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Audit & Tracking - Booking History
          </span>
        }
        extra={
          <Row gutter={16} align="middle">
            <Col>
              <Select
                value={selectedBookingType}
                onChange={setSelectedBookingType}
                style={{ width: 200 }}
              >
                <Option value="OFFLINE_BOOKING">Offline Bookings</Option>
                <Option value="SYSTEM_BOOKING">System Bookings</Option>
                <Option value="MANUAL_BOOKING">Reservations</Option>
                <Option value="GOOGLE_CALENDAR_EVENT">Google Calendar</Option>
              </Select>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchBookings}
                loading={loading}
              >
                Refresh
              </Button>
            </Col>
          </Row>
        }
      >
        <Table
          columns={columns}
          dataSource={bookings}
          rowKey={(record) => record.id || record._id}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} bookings`
          }}
        />
      </Card>

      {/* Audit History Modal */}
      <Modal
        title={
          <span>
            <HistoryOutlined style={{ marginRight: 8 }} />
            Audit History - {selectedBooking?.bookingRef || selectedBooking?.id?.substring(0, 8)}
          </span>
        }
        visible={auditModalVisible}
        onCancel={() => {
          setAuditModalVisible(false);
          dispatch(clearAuditLogs());
        }}
        footer={[
          <Button 
            key="close" 
            onClick={() => {
              setAuditModalVisible(false);
              dispatch(clearAuditLogs());
            }}
          >
            Close
          </Button>
        ]}
        width={800}
      >
        {loadingAudit ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div>
            {selectedBooking && (
              <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <div><strong>Booking ID:</strong> {selectedBooking.id || selectedBooking._id}</div>
                    <div><strong>Type:</strong> {getBookingTypeTag(selectedBooking.bookingType)}</div>
                    <div><strong>Status:</strong> {getBookingStatusTag(selectedBooking.bookingStatus)}</div>
                  </Col>
                  <Col span={12}>
                    <div><strong>Client:</strong> {selectedBooking.user?.name || selectedBooking.clientName}</div>
                    <div><strong>Space:</strong> {selectedBooking.title || selectedBooking.spaceName}</div>
                    <div><strong>Amount:</strong> {selectedBooking.currency || 'LKR'} {selectedBooking.totalPrice}</div>
                  </Col>
                </Row>
              </Card>
            )}

            {auditLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                <div style={{ fontSize: '16px', color: '#595959', marginBottom: '8px' }}>
                  No audit history available
                </div>
                <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                  Audit tracking only records changes made after this feature was enabled.
                  <br />
                  Create, edit, or update this booking to generate audit logs.
                </div>
              </div>
            ) : (
              <Timeline mode="left">
                {auditLogs.map((log, index) => (
                  <Timeline.Item
                    key={log.id || index}
                    color={getAuditActionColor(log.action)}
                    label={moment(log.changedDate).format('DD MMM YYYY HH:mm:ss')}
                  >
                    <Card size="small" style={{ marginBottom: 8 }}>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: '18px', marginRight: 8 }}>
                          {getAuditActionIcon(log.action)}
                        </span>
                        <Tag color={getAuditActionColor(log.action)}>{log.action}</Tag>
                        {log.bookingStatus && getBookingStatusTag(log.bookingStatus)}
                      </div>
                      
                      {log.changedByName && (
                        <div style={{ marginBottom: 4 }}>
                          <UserOutlined /> <strong>Changed By:</strong> {log.changedByName}
                          {log.changedByEmail && <span style={{ color: '#8c8c8c' }}> ({log.changedByEmail})</span>}
                        </div>
                      )}
                      
                      {log.remarks && (
                        <div style={{ marginBottom: 4, color: '#595959' }}>
                          <MessageOutlined /> {log.remarks}
                        </div>
                      )}
                      
                      {renderChangeDetails(log.changes)}
                    </Card>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditTrackingPage;
